<script setup lang="ts">
import Pdf from "@/components/Pdf.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { useVarsityAppConfig } from "@/composables"
import { createInlineConfirmCollection } from "@/composables/createInlineConfirm"
import { type Invitation } from "@/model/app/invitation"
import { TaskViewMode } from "@/pages/Tasks/Issue/util"
import AcceptGenericInvitation from "@/pages/Tasks/Tasks/AccountInvitation/AcceptGenericInvitation.vue"
import AccountInvitationToAdmin from "@/pages/Tasks/Tasks/AccountInvitation/AccountInvitationToAdmin.vue"
import { defineTranslations } from "@/utils/defineTranslations"
import { useHttpClient } from "@/vf"
import { inject, ref } from "vue"
import { type TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<Invitation>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const model = ref({})
const appConfig = useVarsityAppConfig()
const http = useHttpClient()
const viewMode = inject<"customer" | "admin">(TaskViewMode, "customer")

const infos = props.task.infos as {
    text: [
        // translation key
        string,
        // translation params
        Record<string, string>,
    ]
}

const trans = defineTranslations({
    "organization_member.myself": {
        de: 'Um die Einladung anzunehmen gehe zu "Meine Registrierungen" und dort auf "Meine Registrierungen als Person über eine Organisation".',
        en: 'To accept this invitation, go to "My Registrations" and then to "My Registrations as a Person via an Organization".',
    },
    "organization_member.manager": {
        de: 'Um die Einladung anzunehmen gehe zu "Meine Registrierungen" und dort auf "Meine Personen-Verwaltung".',
        en: 'To accept this invitation, go to "My Registrations" and then to "My Person Management".',
    },
    "go_to_my_registrations": {
        de: 'Gehe zu "Meine Registrierungen"',
        en: 'Go to "My Registrations"',
    },
    "delete_invitation": {
        de: "Einladung löschen",
        en: "Delete invitation",
    },
    "delete_invitation.confirmation": {
        de: "Soll die Einladung wirklich gelöscht werden?",
        en: "Do you really want to delete this invitation?",
    },
    "delete_invitation.success": {
        de: "Einladung erfolgreich gelöscht",
        en: "Invitation successfully deleted",
    },
})

async function updated() {
    await appConfig.refreshAppConfig()
    emit("updated")
}

const deleteInvitation = createInlineConfirmCollection<{ id: string }>((id: string) => ({
    async action() {
        await http.delete(`/invitation/${id}`)
    },
    afterAction() {
        emit("updated")
    },
}))
</script>

<template>
    <div class="p-4">
        <template v-if="infos?.text">
            {{ $t(infos.text[0], infos.text[1], props.task.numberOfAccounts ?? 0) }}
        </template>

        <div v-if="viewMode === 'customer' && props.task.state === 'open'" class="mt-4">
            <div class="mt-3" v-if="props.task.accountType == 'Organization'">
                <pdf file="/legal/show/General.Organization/pdf"></pdf>
            </div>
            <div v-if="props.task.accountType == 'Admin'" class="mb-3">
                <AccountInvitationToAdmin
                    @update:model-value="model = $event"
                    @updated="emit('updated')"
                    v-bind="props"
                ></AccountInvitationToAdmin>
            </div>

            <div v-else-if="props.task.accountType === 'OrganizationMember' && props.task.managementType === 'myself'">
                <div class="mb-3">
                    {{ trans("organization_member.myself") }}
                </div>

                <RouterLink class="btn btn-primary" :to="{ name: '@App.Organization.MyOwnMemberships' }">
                    {{ trans("go_to_my_registrations") }}
                </RouterLink>
            </div>

            <div v-else-if="props.task.accountType === 'OrganizationMember' && props.task.managementType === 'manager'">
                <div class="mb-3">
                    {{ trans("organization_member.manager") }}
                </div>

                <RouterLink class="btn btn-primary" :to="{ name: '@App.Organization.MyManagedMemberships' }">
                    {{ trans("go_to_my_registrations") }}
                </RouterLink>
            </div>

            <div v-else>
                <AcceptGenericInvitation @updated="updated" v-bind="props"></AcceptGenericInvitation>
            </div>
        </div>
    </div>
    <Teleport to="#taskButtons" v-if="viewMode == 'admin'">
        <VarsityInlineConfirmButton class="btn btn-danger" :controller="deleteInvitation(task.id!)">
            <i class="fa fa-fw fa-trash"></i>
            {{ trans("delete_invitation") }}
        </VarsityInlineConfirmButton>
    </Teleport>
    <Teleport to="#taskConfirmation" v-if="viewMode == 'admin'">
        <VarsityInlineConfirm :controller="deleteInvitation(task.id!)">
            <template #confirmation>
                {{ trans("delete_invitation.confirmation") }}
            </template>
            <template #success>
                {{ trans("delete_invitation.success") }}
            </template>
        </VarsityInlineConfirm>
    </Teleport>
</template>
